import React, { useState } from "react";
import {
  Button,
  Chip,
  Grid,
  Paper,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import { toast } from "react-toastify";

import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { getLeadCreativeName } from "../../utils/pixelCreative";

const getTodaySaoPaulo = () =>
  new Date().toLocaleDateString("en-CA", { timeZone: "America/Sao_Paulo" });

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
  },
  resultBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.action.hover,
  },
  chipRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(1),
  },
  actions: {
    display: "flex",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
    flexWrap: "wrap",
  },
}));

const renderLeadStatus = (lead) => {
  const purchaseCount = lead?.purchase_count || 0;
  const totalValue = Number(lead?.sale_value || 0);

  if (purchaseCount > 0) {
    return (
      <Chip
        size="small"
        color="primary"
        label={i18n.t("pixel.status.purchasesSummary", {
          count: purchaseCount,
          value: totalValue.toFixed(2),
        })}
      />
    );
  }

  if (lead?.pixel_complete) {
    return (
      <Chip
        size="small"
        style={{ backgroundColor: "#10b981", color: "#fff" }}
        label={i18n.t("pixel.status.complete")}
      />
    );
  }

  return (
    <Chip
      size="small"
      style={{ backgroundColor: "#f59e0b", color: "#fff" }}
      label={i18n.t("pixel.status.incomplete")}
    />
  );
};

const PixelManualSearch = () => {
  const classes = useStyles();
  const [date, setDate] = useState(getTodaySaoPaulo());
  const [message, setMessage] = useState("");
  const [phone, setPhone] = useState("");
  const [value, setValue] = useState("");
  const [lead, setLead] = useState(null);
  const [searching, setSearching] = useState(false);
  const [sending, setSending] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const handleSearch = async () => {
    const trimmedMessage = message.trim();
    if (!trimmedMessage) {
      toast.error(i18n.t("pixel.manual.messageRequired"));
      return;
    }

    if (!date) {
      toast.error(i18n.t("pixel.manual.dateRequired"));
      return;
    }

    setSearching(true);
    setLead(null);
    setNotFound(false);

    try {
      const { data } = await api.post("/pixel/lookup", {
        message: trimmedMessage,
        date,
      });

      if (data.found && data.lead) {
        setLead(data.lead);
        const defaultPhone = data.lead.sale_phone || data.lead.phone_wpp || "";
        setPhone(defaultPhone ? `+${String(defaultPhone).replace(/^\+?/, "")}` : "");
      } else {
        setNotFound(true);
      }
    } catch (err) {
      toastError(err);
    } finally {
      setSearching(false);
    }
  };

  const handleSend = async () => {
    if (!lead?.id) {
      return;
    }

    const numericValue = parseFloat(String(value).replace(",", "."));
    if (!numericValue || numericValue <= 0) {
      toast.error(i18n.t("pixel.modal.invalidValue"));
      return;
    }

    if (!phone.trim()) {
      toast.error(i18n.t("pixel.manual.phoneRequired"));
      return;
    }

    setSending(true);
    try {
      const { data } = await api.post("/pixel/manual-purchase", {
        leadId: lead.id,
        phone,
        value: numericValue,
      });

      if (data.lead) {
        setLead(data.lead);
      }

      const purchaseCount = data.lead?.purchase_count || data.purchase_count || 0;
      toast.success(
        purchaseCount > 1
          ? i18n.t("pixel.modal.upsellSuccess")
          : i18n.t("pixel.modal.success")
      );
      setValue("");
    } catch (err) {
      toastError(err);
    } finally {
      setSending(false);
    }
  };

  const missingFields = lead?.missing_fields || [];
  const creativeName = getLeadCreativeName(lead);

  return (
    <Paper className={classes.paper} variant="outlined">
      <Typography variant="h6" className={classes.sectionTitle}>
        {i18n.t("pixel.manual.title")}
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <TextField
            fullWidth
            type="date"
            label={i18n.t("pixel.manual.date")}
            value={date}
            onChange={(e) => setDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>
        <Grid item xs={12} md={9}>
          <TextField
            fullWidth
            multiline
            minRows={2}
            label={i18n.t("pixel.manual.message")}
            placeholder={i18n.t("pixel.manual.messagePlaceholder")}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
        </Grid>
      </Grid>

      <div className={classes.actions}>
        <ButtonWithSpinner
          loading={searching}
          variant="contained"
          color="primary"
          startIcon={<SearchIcon />}
          onClick={handleSearch}
        >
          {i18n.t("pixel.manual.search")}
        </ButtonWithSpinner>
      </div>

      {notFound && (
        <Typography variant="body2" color="error" style={{ marginTop: 16 }}>
          {i18n.t("pixel.manual.notFound")}
        </Typography>
      )}

      {lead && (
        <div className={classes.resultBox}>
          {renderLeadStatus(lead)}
          <Typography variant="body2" style={{ marginTop: 8 }}>
            <strong>{i18n.t("pixel.modal.message")}:</strong> {lead.message}
          </Typography>
          {creativeName && (
            <Typography variant="body2" style={{ marginTop: 8 }}>
              <strong>{i18n.t("pixel.creative")}:</strong> {creativeName}
            </Typography>
          )}
          {lead.utm_campaign && (
            <Typography variant="body2">
              <strong>UTM:</strong> {lead.utm_campaign}
            </Typography>
          )}
          {lead.created_at && (
            <Typography variant="body2">
              <strong>{i18n.t("pixel.modal.leadDate")}:</strong>{" "}
              {lead.created_at.substring(0, 16).replace("T", " ")}
            </Typography>
          )}
          {missingFields.length > 0 && (
            <div className={classes.chipRow}>
              {missingFields.map((field) => (
                <Chip key={field} size="small" variant="outlined" label={field} />
              ))}
            </div>
          )}

          <Grid container spacing={2} style={{ marginTop: 8 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={i18n.t("pixel.manual.phone")}
                placeholder="+351 9XX XXX XXX"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                inputProps={{ min: 0, step: "0.01" }}
                label={i18n.t("pixel.modal.value")}
                placeholder="0.00"
                value={value}
                onChange={(e) => setValue(e.target.value)}
              />
            </Grid>
          </Grid>

          <div className={classes.actions}>
            <ButtonWithSpinner
              loading={sending}
              variant="contained"
              color="primary"
              onClick={handleSend}
            >
              {(lead.purchase_count || 0) > 0
                ? i18n.t("pixel.modal.sendUpsell")
                : i18n.t("pixel.modal.send")}
            </ButtonWithSpinner>
            <Button onClick={() => setLead(null)} disabled={sending}>
              {i18n.t("pixel.manual.clear")}
            </Button>
          </div>
        </div>
      )}
    </Paper>
  );
};

export default PixelManualSearch;
